import { useEffect, useState } from "react";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  TableContainer,
  Snackbar,
  Typography,
  Checkbox,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../molecules/PageTitle";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { formatDate } from "../../../common/const";
import BackButton from "../../atoms/Button/BrowserBackButton";
import { CustomContainer } from "../../../styles/styles";
import { UUID_CHARACTER_LIMIT } from "../../../utils/ValidationHelper";
import RequiredLabel from "../../atoms/RequiredLabel";
import React from "react";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import { HaudiUuid } from "../../../utils/HaudiUUID";
import AutomaticallyCalculatedLabel from "../../atoms/AutomaticallyCalculatedLabel";
import {
  ErrorWithRetry,
  LoadingSpinner,
} from "../../molecules/LoadingAndError";

interface Building {
  autoBuildingId: number;
  buildingId: string;
  buildingManagementId: string;
  buildingName: string;
  buildingAddress: string;
  owner: Owner | null;
}

interface Owner {
  ownerId: string;
  ownerName: string;
}

interface SDCDetails {
  autoSdcId: number;
  sdcUuid: string;
  sdcSerialNumber: string;
  shippingDate: string;
  createdAt: string;
  firmwareVersion: string;
  firmwareFile: File | null;
  firmUpdateDate: Date | undefined;
  firmLastBootDate: Date | undefined;
  wifiMacAddress: string;
  bluetoothDeviceAddress: string;
  wanPriority: string;
  isLteEnabled: boolean;
  lteApn: string;
  lteId: string;
  ltePassword: string;
  lteAuthType: string;
  isWifiEnabled: boolean;
  defaultWifi: number;
  ap1Ssid: string;
  ap1Password: string;
  ap1IpConfigType: string;
  ap1StaticIp: string | null;
  ap1StaticGateWay: string | null;
  ap1StaticDns: string | null;
  ap2Ssid: string;
  ap2Password: string;
  ap2IpConfigType: string;
  ap2StaticIp: string | null;
  ap2StaticGateWay: string | null;
  ap2StaticDns: string | null;
  ap3Ssid: string;
  ap3Password: string;
  ap3IpConfigType: string;
  ap3StaticIp: string | null;
  ap3StaticGateWay: string | null;
  ap3StaticDns: string | null;
  isBleEnabled: boolean;
  iBeaconMajor: string;
  iBeaconMinor: string;
  isAalEnabled: boolean;
  isElectronicLockEnabled: boolean;
  isNfcEnabled: boolean;
  isNfcCertRequired: boolean;
  isSciEnabled: boolean;
  isGuideBellEnabled: boolean;
  isSpeakerEnabled: boolean;
  isSignageEnabled: boolean;
  isDefaultOpenHalf: boolean;
  isPushButtonEnabled: boolean;
  sciSignageIpAddr: string;
  sciSpeakerUuid: string;
  idInCluster: number;
  isEnabled: boolean;
  doorPlace: string;
  installationDate: string;
  memo: string;
  building: Building | null;
  simId: string;
  simStatus: string;
  simOperation: string;
}

const SDCEditPage = () => {
  const navigate = useNavigate();
  const { id, autoSdcId } = useParams();
  const [data, setData] = useState<SDCDetails | undefined>(undefined);
  const [uuid, setUuid] = useState("");
  const [haudiUuid, setHaudiUuid] = useState<HaudiUuid | undefined>(undefined);
  const [shippingDate, setShippingDate] = useState<Date | undefined>(undefined);
  const [isBroken, setIsBroken] = useState<boolean | undefined>(undefined);
  const [memo, setMemo] = useState("");
  const [fetchedTs, setFetchedTs] = useState<number>(0);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");

  // SIM操作関連
  const [simId, setSimId] = useState("");
  const [simConfirmOpen, setSimConfirmOpen] = useState(false);
  const [simOperationType, setSimOperationType] = useState<
    "waiting_operation" | "waiting_running"
  >("waiting_operation");

  const getSimOperationStatus = (status: string) => {
    switch (status) {
      case "waiting_operation":
        return "利用中断中";
      case "waiting_running":
        return "運用待ち";
      case "in_running":
        return "運用中";
      case "testing":
        return "試験中";
      default:
        return status || "-";
    }
  };

  const handleSimOperation = async (
    operation: "waiting_operation" | "waiting_running"
  ) => {
    setSimOperationType(operation);
    setSimConfirmOpen(true);
  };
  const executeSimOperation = async () => {
    try {
      const jwtToken = await getJwtTokenFromIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/sdcs/${autoSdcId}/sim`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({
            simId: data?.simId,
            nextSimOperation: simOperationType,
          }),
        }
      );

      if (response.ok) {
        setSnackbarMessage("SIM操作が成功しました。");
        setAlertSeverity("success");
        setSnackbarOpen(true);
        // データを再取得して画面を更新
        fetchData();
      } else {
        throw new Error("SIM操作に失敗しました。");
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage("SIM操作に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSimConfirmOpen(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  const [sdcDeleteConfirm, setSdcDeleteConfirm] = React.useState(false);
  const [sdcDeletedConfirm, setSdcDeletedConfirm] = React.useState(false);

  const CHARACTER_LIMIT = 50; // 文字数制限を定義

  const [validationErrors, setValidationErrors] = useState({
    uuid: "",
    shippingDate: "",
    memo: "",
    simId: "",
  });

  const handleValidation = () => {
    const errors = {
      uuid: uuid.length > UUID_CHARACTER_LIMIT
        ? `UUIDは${UUID_CHARACTER_LIMIT}文字以内で入力してください`
        : "",
      shippingDate: "",
      memo:
        memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください`
          : "",
      simId: simId.length > 20 ? "SIM IDは20文字以内で入力してください" : "",
    };
    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  // UUID専用の入力ハンドラを作成
  const handleUuidChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setUuid(value);
    // UUID用のバリデーションを実行
    if (!value) {
      setValidationErrors((prev) => ({
        ...prev,
        uuid: "UUIDは必須です",
      }));
    } else if (value.length > UUID_CHARACTER_LIMIT) {
      setValidationErrors((prev) => ({
        ...prev,
        uuid: `UUIDは${UUID_CHARACTER_LIMIT}文字以内で入力してください`,
      }));
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        uuid: "",
      }));
    }
  };

  // 元のhandleInputChangeからUUIDの処理を削除
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "memo") {
      setMemo(value);
    } else if (name === "simId") {
      setSimId(value);
    }
    handleValidation();
  };

  const fetchData = async () => {
    setIsLoading(true);
    setHasError(false);
    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    try {
      // call showSdc API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + `/sdcs/${autoSdcId}`,
        {
          headers: {
            Authorization: jwtToken, // Authorizationヘッダーを追加
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setFetchedTs(Date.now()); // データ取得時にタイムスタンプを設定
      const jsonResponse = await response.json();
      setData(jsonResponse);
      setMemo(jsonResponse.memo);
      setShippingDate(jsonResponse.shippingDate);
      setIsBroken(jsonResponse.isBroken);
      setUuid(jsonResponse.sdcUuid);
      setSimId(jsonResponse.simId || "");

      console.log("jsonResponse:" + JSON.stringify(jsonResponse));
    } catch (error) {
      console.error("Error:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [autoSdcId]);

  const handleUpdateClick = async () => {
    setIsSubmitting(true);
    console.log(`SDC詳細の更新がクリックされました`);

    // もしバリデーションエラーがある場合は処理を中断
    if (!handleValidation()) {
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // 更新するデータを取得
    const requestData = {
      lastTsFetchedAt: fetchedTs,
      sdc: {
        // sdcオブジェクトとしてラップ
        sdcUuid: uuid,
        shippingDate: shippingDate,
        isBroken: isBroken,
        memo: memo,
        simId: simId,
      },
    };

    try {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      // call updateSdc API (version=2を追加)
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + `/sdcs/${autoSdcId}?version=2`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log("SDC詳細の更新が成功しました");
      setSnackbarMessage("SDC詳細の更新が成功しました");
      setAlertSeverity("success");
      setSnackbarOpen(true);
      setFetchedTs(Date.now()); // 更新成功時にタイムスタンプを更新
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage("更新に失敗しました");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    console.log(`SDCの削除がクリックされました`);

    try {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      // call deleteSdc API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + `/sdcs/${autoSdcId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: jwtToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log("SDCの削除が成功しました");
      setSdcDeletedConfirm(true);
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage("削除に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelClick = () => {
    console.log(`キャンセルがクリックされました：${id}`);
    navigate(-1);
  };

  useEffect(() => {
    if (data !== undefined) {
      setHaudiUuid(
        new HaudiUuid(uuid, data.isAalEnabled, data.isSciEnabled, {
          isGuideBellEnabled: data.isGuideBellEnabled,
          isSignageEnabled: data.isSignageEnabled,
          isSpeakerEnabled: data.isSpeakerEnabled,
          idInCluster: data.idInCluster,
        })
      );
    }
  }, [uuid]); // 依存配列はuuidのみにする

  // 初回データ取得時にもHaudiUuidを設定
  useEffect(() => {
    fetchData().then(() => {
      if (data !== undefined) {
        setHaudiUuid(
          new HaudiUuid(uuid, data.isAalEnabled, data.isSciEnabled, {
            isGuideBellEnabled: data.isGuideBellEnabled,
            isSignageEnabled: data.isSignageEnabled,
            isSpeakerEnabled: data.isSpeakerEnabled,
            idInCluster: data.idInCluster,
          })
        );
      }
    });
  }, []); // 初回のみ実行

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return <ErrorWithRetry onRetry={fetchData} />;
  }

  return (
    <CustomContainer maxWidth="lg">
      <Grid item xs={12} marginBottom={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <PageTitle title={"SDC詳細"} />
          <BackButton />
        </Box>
      </Grid>
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>UUID（ハウディBase64）</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  label=""
                  name="UUID"
                  value={uuid}
                  onChange={handleUuidChange}
                  size="small"
                  error={!!validationErrors.uuid}
                  helperText={validationErrors.uuid}
                  disabled
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>RFC UUID（デバッグ用）</TableCell>
              <TableCell>
                <Typography fontFamily="monospace">
                  {haudiUuid?.getRfcFormattedUuid()}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>SDCSN</TableCell>
              <TableCell>{data?.sdcSerialNumber}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>登録日</TableCell>
              <TableCell>
                {data?.createdAt ? new Date(data.createdAt).toLocaleDateString("ja-JP", {
                  timeZone: "Asia/Tokyo",
                }) : ""}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>出荷日</TableCell>
              <TableCell>
                <Box>
                  <TextField
                  label="出荷日"
                  type="date"
                  value={shippingDate ? formatDate(shippingDate) : ""}
                  size="small"
                  onChange={(e) => {
                    setShippingDate(new Date(e.target.value));
                    handleValidation();
                  }}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.shippingDate}
                  helperText={validationErrors.shippingDate}
                  />
                  <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                    出荷日を設定すると「SDC設置・LAD契約者（オーナー）管理」アプリ上にSDCが表示されます。
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>SDCを故障中に設定する</TableCell>
              <TableCell>
                <Checkbox
                  checked={isBroken || false}
                  onChange={(e) => setIsBroken(e.target.checked)}
                  color="primary"
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>FirmWare Ver</TableCell>
              <TableCell>{data?.firmwareVersion}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Wi-Fi MAC</TableCell>
              <TableCell>{data?.wifiMacAddress}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>bluetoothDeviceAddress</TableCell>
              <TableCell>{data?.bluetoothDeviceAddress}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>オーナID</TableCell>
              <TableCell>{data?.building?.owner?.ownerId}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>オーナ名称</TableCell>
              <TableCell>{data?.building?.owner?.ownerName}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>物件システムID</TableCell>
              <TableCell>{data?.building?.buildingId}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>取付先コード（物件管理ID）</TableCell>
              <TableCell>{data?.building?.buildingManagementId}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>取付先名（物件名称）</TableCell>
              <TableCell>{data?.building?.buildingName}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>物件住所</TableCell>
              <TableCell>{data?.building?.buildingAddress}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>メモ</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  multiline
                  label=""
                  name="memo"
                  value={memo}
                  onChange={handleInputChange}
                  error={!!validationErrors.memo}
                  helperText={validationErrors.memo}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>優先WAN</TableCell>
              <TableCell>{data?.wanPriority}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>LTE</TableCell>
              <TableCell>{data?.isLteEnabled ? "有効" : "無効"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>LTE APN</TableCell>
              <TableCell>{data?.lteApn}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>LTE ID</TableCell>
              <TableCell>{data?.lteId}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>LTE パスワード</TableCell>
              <TableCell>{data?.ltePassword}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>LTE 認証タイプ</TableCell>
              <TableCell>{data?.lteAuthType}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>SIM ID</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  label=""
                  name="simId"
                  value={simId}
                  onChange={handleInputChange}
                  size="small"
                  error={!!validationErrors.simId}
                  helperText={validationErrors.simId}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>SIM Status</TableCell>
              <TableCell>{data?.simStatus || "-"}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>SIM Operation / 操作</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Typography style={{ fontSize: "14px", marginRight: "16px" }}>
                    {getSimOperationStatus(data?.simOperation || "")}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleSimOperation("waiting_operation")}
                    style={{ marginRight: "8px" }}
                  >
                    利用中断（suspended）に変更
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleSimOperation("waiting_running")}
                  >
                    運用待ち（stand-by）に変更
                  </Button>
                </Box>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Wi-Fi</TableCell>
              <TableCell>{data?.isWifiEnabled ? "有効" : "無効"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>有線接続AP</TableCell>
              <TableCell>{data?.defaultWifi}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP1 SSID</TableCell>
              <TableCell>{data?.ap1Ssid}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP1 パスワード</TableCell>
              <TableCell>{data?.ap1Password}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP1 IP設定タイプ</TableCell>
              <TableCell>{data?.ap1IpConfigType}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP1 固定IPアドレス</TableCell>
              <TableCell>{data?.ap1StaticIp}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP1 ゲートウェイ</TableCell>
              <TableCell>{data?.ap1StaticGateWay}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP1 DNS</TableCell>
              <TableCell>{data?.ap1StaticDns}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP2 SSID</TableCell>
              <TableCell>{data?.ap2Ssid}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP2 パスワード</TableCell>
              <TableCell>{data?.ap2Password}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP2 IP設定タイプ</TableCell>
              <TableCell>{data?.ap2IpConfigType}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP2 固定IPアドレス</TableCell>
              <TableCell>{data?.ap2StaticIp}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP2 ゲートウェイ</TableCell>
              <TableCell>{data?.ap2StaticGateWay}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP2 DNS</TableCell>
              <TableCell>{data?.ap2StaticDns}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP3 SSID</TableCell>
              <TableCell>{data?.ap3Ssid}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP3 パスワード</TableCell>
              <TableCell>{data?.ap3Password}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP3 IP設定タイプ</TableCell>
              <TableCell>{data?.ap3IpConfigType}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP3 固定IPアドレス</TableCell>
              <TableCell>{data?.ap3StaticIp}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP3 ゲートウェイ</TableCell>
              <TableCell>{data?.ap3StaticGateWay}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>AP3 DNS</TableCell>
              <TableCell>{data?.ap3StaticDns}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>BLE</TableCell>
              <TableCell>{data?.isBleEnabled ? "有効" : "無効"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>
                iBeacon Major
                {<AutomaticallyCalculatedLabel />}
              </TableCell>
              <TableCell>
                <Typography fontFamily="monospace">
                  {haudiUuid?.getMajorValue().toString("hex").padStart(2, "0")}
                </Typography>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>
                iBeacon Minor
                {<AutomaticallyCalculatedLabel />}
              </TableCell>
              <TableCell>
                <Typography fontFamily="monospace">
                  {haudiUuid?.getMinorValue().toString("hex").padStart(2, "0")}
                </Typography>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>電子錠</TableCell>
              <TableCell>
                {data?.isElectronicLockEnabled ? "有効" : "無効"}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>NFC</TableCell>
              <TableCell>{data?.isNfcEnabled ? "有効" : "無効"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>NFCセキュリティ強化</TableCell>
              <TableCell>{data?.isNfcCertRequired ? "有効" : "無効"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>ミライロドア機能</TableCell>
              <TableCell>{data?.isSciEnabled ? "有効" : "無効"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>スピーカ設置</TableCell>
              <TableCell>
                {data?.isGuideBellEnabled ? "有効" : "無効"}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>サイネージ</TableCell>
              <TableCell>{data?.isSignageEnabled ? "有効" : "無効"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>サイネージIPアドレス</TableCell>
              <TableCell>{data?.sciSignageIpAddr}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>事務所SP（介助要請モニタ）</TableCell>
              <TableCell>{data?.isSpeakerEnabled ? "使用する" : "使用しない"}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>事務所SP UUID</TableCell>
              <TableCell>{data?.sciSpeakerUuid}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>ミライロドア用ドア区別番号</TableCell>
              <TableCell>{data?.idInCluster}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>デフォルト半開</TableCell>
              <TableCell>{data?.isDefaultOpenHalf ? "有効" : "無効"}</TableCell>
            </TableRow>

            {/* <TableRow>
              <TableCell>タッチスイッチの有無</TableCell>
              <TableCell>
                {data?.isPushButtonEnabled ? "有り" : "無し"}
              </TableCell>
            </TableRow> */}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button variant="outlined" color="inherit" onClick={handleCancelClick}>
          キャンセル
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={(e) => {
            if (isDeleting) return;
            setSdcDeleteConfirm(true);
          }}
          style={{ marginLeft: 8 }}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              削除中...
            </Box>
          ) : (
            "削除"
          )}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={(e) => {
            if (isSubmitting) return;
            handleUpdateClick();
          }}
          style={{ marginLeft: 8 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              更新中...
            </Box>
          ) : (
            "更新"
          )}
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={sdcDeleteConfirm}
        onClose={() => {
          setSdcDeleteConfirm(false);
        }}
        onConfirm={async () => {
          handleDeleteClick();
        }}
        title="確認"
        description="SDCに紐づく鍵も削除されますが、本当に削除しますか？"
      />

      <ConfirmDialog
        open={sdcDeletedConfirm}
        onClose={() => {
          setSdcDeletedConfirm(false);
        }}
        onConfirm={() => {
          navigate(`/sdcs`); // 削除が成功したら一覧ページに戻る
        }}
        title="確認"
        description="削除が完了しました。"
        showCancelButton={false}
      />

      <ConfirmDialog
        open={simConfirmOpen}
        onClose={() => setSimConfirmOpen(false)}
        onConfirm={executeSimOperation}
        title="SIM操作の確認"
        description={`SIMを${
          simOperationType === "waiting_operation" ? "利用中断" : "運用待ち"
        }に変更しますか？`}
      />
    </CustomContainer>
  );
};

export default SDCEditPage;
